<template>
  <div class="ab-page">
    <div class="ab-header">
      <h4 class="ab-title">Address Book</h4>
      <div class="ab-actions">
        <select v-model="filterChain" class="form-control form-control-sm" style="width:120px">
          <option value="">All chains</option>
          <option value="tron">Tron</option>
          <option value="ethereum">Ethereum</option>
          <option value="bsc">BSC</option>
          <option value="polygon">Polygon</option>
          <option value="solana">Solana</option>
          <option value="all">Multi-chain</option>
        </select>
        <input
          v-model="search"
          class="form-control form-control-sm"
          placeholder="Search label or address…"
          style="width:220px"
        />
        <button class="btn btn-sm btn-primary" @click="startAdd">+ Add Address</button>
        <button class="btn btn-sm btn-secondary" @click="load">↺</button>
      </div>
    </div>

    <!-- Add / Edit form -->
    <div v-if="editing" class="ab-form card mb-3">
      <div class="card-body">
        <div class="ab-form-row">
          <div class="ab-form-col">
            <label>Label</label>
            <input v-model="editing.label" class="form-control form-control-sm" placeholder="My cold wallet" />
          </div>
          <div class="ab-form-col" style="flex:2">
            <label>Address</label>
            <input v-model="editing.address" class="form-control form-control-sm" placeholder="0x… or T…" />
          </div>
          <div class="ab-form-col">
            <label>Chain</label>
            <select v-model="editing.chain" class="form-control form-control-sm">
              <option value="all">Multi-chain</option>
              <option value="tron">Tron</option>
              <option value="ethereum">Ethereum</option>
              <option value="bsc">BSC</option>
              <option value="polygon">Polygon</option>
              <option value="solana">Solana</option>
            </select>
          </div>
        </div>
        <div class="ab-form-row mt-2">
          <div class="ab-form-col" style="flex:1">
            <label>Notes</label>
            <input v-model="editing.notes" class="form-control form-control-sm" placeholder="Optional note" />
          </div>
        </div>
        <div class="mt-3" style="display:flex; gap:8px">
          <button class="btn btn-sm btn-primary" :disabled="saving" @click="saveEntry">
            {{ saving ? 'Saving…' : (editing.id ? 'Update' : 'Save') }}
          </button>
          <button class="btn btn-sm btn-secondary" @click="editing = null">Cancel</button>
        </div>
        <p v-if="formError" class="text-danger small mt-2">{{ formError }}</p>
      </div>
    </div>

    <PageLoader v-if="loading" message="Loading address book…" />
    <div v-else-if="!rows.length" class="ab-empty">
      No saved addresses yet. Click <strong>+ Add Address</strong> to start.
    </div>
    <div v-else class="ab-table-wrap">
      <table class="table table-sm table-hover">
        <thead>
          <tr>
            <th>Label</th>
            <th>Address</th>
            <th>Chain</th>
            <th>AML Risk</th>
            <th>Notes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id">
            <td class="td-label">{{ r.label || '—' }}</td>
            <td class="td-addr" :title="r.address">{{ r.address }}</td>
            <td>
              <span class="chain-pill" :style="`color:${chainColor(r.chain)}`">
                {{ r.chain || '—' }}
              </span>
            </td>
            <td>
              <span class="risk-badge" :class="`risk-${r.aml_risk_level || 'unknown'}`">
                {{ (r.aml_risk_level || 'unknown').toUpperCase() }}
              </span>
            </td>
            <td class="td-notes">{{ r.notes || '' }}</td>
            <td>
              <div style="display:flex; gap:6px">
                <button class="btn btn-sm btn-secondary" @click="startEdit(r)" title="Edit">✎</button>
                <button class="btn btn-sm btn-danger" @click="remove(r)" title="Delete">✕</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getAddresses, saveAddress, updateAddress, deleteAddress } from '../services/pbService';
import PageLoader from '../components/PageLoader.vue';

const CHAIN_COLOR = {
  tron: '#FF0013', ethereum: '#627EEA', bsc: '#F0B90B', polygon: '#8247E5', solana: '#9945FF', all: '#6366f1'
};

export default {
  name: 'AddressBook',
  components: { PageLoader },
  data() {
    return {
      allEntries: [],
      loading: false,
      search: '',
      filterChain: '',
      editing: null,
      saving: false,
      formError: '',
    };
  },
  computed: {
    rows() {
      const q = this.search.toLowerCase();
      return this.allEntries.filter(r => {
        if (this.filterChain && r.chain !== this.filterChain) return false;
        if (q && !r.label?.toLowerCase().includes(q) && !r.address?.toLowerCase().includes(q)) return false;
        return true;
      });
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await getAddresses();
        this.allEntries = res.items;
      } catch (e) {
        console.error('Failed to load addresses:', e);
      } finally {
        this.loading = false;
      }
    },
    startAdd() {
      this.editing = { label: '', address: '', chain: 'all', notes: '', aml_risk_level: 'unknown' };
      this.formError = '';
    },
    startEdit(r) {
      this.editing = { ...r };
      this.formError = '';
    },
    async saveEntry() {
      if (!this.editing.address.trim()) {
        this.formError = 'Address is required.';
        return;
      }
      this.saving = true;
      this.formError = '';
      try {
        if (this.editing.id) {
          const { id, ...data } = this.editing;
          await updateAddress(id, data);
        } else {
          await saveAddress(this.editing);
        }
        await this.load();
        this.editing = null;
      } catch (e) {
        this.formError = e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },
    async remove(r) {
      if (!confirm(`Delete "${r.label || r.address}"?`)) return;
      try {
        await deleteAddress(r.id);
        this.allEntries = this.allEntries.filter(e => e.id !== r.id);
      } catch (e) {
        alert('Delete failed: ' + e.message);
      }
    },
    chainColor(chain) { return CHAIN_COLOR[chain] || '#aaa'; },
  },
};
</script>

<style scoped>
.ab-page { padding: 0; }

.ab-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.ab-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.01em;
  margin: 0;
  flex-shrink: 0;
}

.ab-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.ab-empty {
  text-align: center;
  color: var(--text-muted);
  padding: 60px 0;
  font-size: 0.88rem;
}

.ab-form { background: var(--surface-2) !important; }

.ab-form-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ab-form-col {
  flex: 1;
  min-width: 150px;
}

.ab-form-col label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-muted);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.ab-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.td-label  { font-weight: 600; }
.td-addr   { font-family: monospace; font-size: 0.79rem; word-break: break-all; }
.td-notes  { font-size: 0.78rem; color: var(--text-muted); }

.chain-pill {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.risk-badge {
  font-size: 0.67rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
  letter-spacing: 0.05em;
}
.risk-unknown  { background: rgba(108,117,125,0.15); color: #9ca3af; }
.risk-low      { background: rgba(40,167,69,0.15);   color: #86efac; }
.risk-medium   { background: rgba(255,193,7,0.15);   color: #fcd34d; }
.risk-high     { background: rgba(253,126,20,0.15);  color: #fdba74; }
.risk-critical { background: rgba(220,53,69,0.15);   color: #fca5a5; }
</style>
